import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import * as XLSX from 'xlsx';
import { Container, Button, Image } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';

const BDEmpleados = (props) => {
  const { role: userRole } = useAuth();
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [selectedEmpleado, setSelectedEmpleado] = useState({
    id: null,
    nombre: '',
    apellido: '',
    cargo: '',
    dni: '',
    foto: '', // base64
  });

  // Manejar cambio de imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedEmpleado(prev => ({
          ...prev,
          foto: reader.result, // base64
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchEmpleados = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://us-central1-inprojuy-f1173.cloudfunctions.net/app/empleados');
      setEmpleados(response.data);
    } catch (error) {
      console.error('Error al obtener los datos de empleados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmpleados();
  }, []);

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedEmpleado(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEdit = (empleado) => {
    setSelectedEmpleado({ ...empleado, foto: empleado.foto || '' });
    handleShowModal();
  };

  const handleShowDetail = (empleado) => {
    setSelectedEmpleado({ ...empleado });
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este empleado?")) {
      try {
        await axios.delete(`https://us-central1-inprojuy-f1173.cloudfunctions.net/app/empleados/${id}`);
        fetchEmpleados();
      } catch (error) {
        console.error("Error al eliminar el empleado:", error);
        alert("Error al eliminar el empleado");
      }
    }
  };

  const handleAddEmpleado = () => {
    setSelectedEmpleado({ id: null, nombre: '', apellido: '', cargo: '', dni: '', foto: '' });
    handleShowModal();
  };

  const handleSave = async () => {
    // Validación de campos obligatorios
    const { nombre, apellido, cargo, dni } = selectedEmpleado;
    if (!nombre || !apellido || !cargo || !dni) {
      alert('Todos los campos son obligatorios.');
      return;
    }
    if (saving) return; // anti doble-submit: evita empleados duplicados
    setSaving(true);
    try {
      if (selectedEmpleado.id) {
        await axios.put(`https://us-central1-inprojuy-f1173.cloudfunctions.net/app/empleados/${selectedEmpleado.id}`, selectedEmpleado);
        alert("Empleado actualizado correctamente");
      } else {
        await axios.post('https://us-central1-inprojuy-f1173.cloudfunctions.net/app/agregarempleado', selectedEmpleado);
        alert("Empleado agregado correctamente");
      }
      fetchEmpleados();
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar el empleado:", error);
      alert("Error al guardar el empleado");
    } finally {
      setSaving(false);
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(empleados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Empleados");
    XLSX.writeFile(wb, 'empleados.xlsx');
  };

  const columns = [
    { field: 'nombre', headerName: 'Nombre', width: 130 },
    { field: 'apellido', headerName: 'Apellido', width: 130 },
    { field: 'cargo', headerName: 'Cargo', width: 130 },
    { field: 'dni', headerName: 'DNI', width: 130 },
    {
      field: 'verDetalle',
      headerName: 'Ver Detalle',
      width: 130,
      renderCell: (params) => (
        <Button variant="info" size="sm" onClick={() => handleShowDetail(params.row)}>
          Ver Detalle
        </Button>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      renderCell: (params) => (
        userRole === 'admin' ? (
          <div>
            <Button variant="outline-primary" size="sm" onClick={() => handleEdit(params.row)}>
              Editar
            </Button>{' '}
            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(params.row.id)}>
              Eliminar
            </Button>
          </div>
        ) : null
      ),
    },
  ];

  return (
    <Container style={{ height: 650, width: '100%' }}>
      <div style={{ height: 'auto', width: '100%' }}>
        <center><h2>Base de Datos de Empleados</h2></center>
        <p></p>
        <Button onClick={handleAddEmpleado} variant="primary">Agregar Empleado</Button>{' '}
        <Button onClick={exportToExcel} variant="success">Exportar a Excel</Button>
        <p></p>

        {loading ? (
          <div id="load">
            <div>O</div><div>D</div><div>N</div><div>A</div><div>G</div><div>R</div><div>A</div><div>C</div>
          </div>
        ) : (
          <div style={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={empleados}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 20]}
              pagination
              getRowId={(row) => row.id ?? row.dni ?? Math.random().toString(36).substr(2, 9)}
            />
          </div>
        )}

        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{selectedEmpleado.id ? 'Editar Empleado' : 'Agregar Empleado'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group controlId="formFoto">
                <Form.Label>Foto del empleado</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {selectedEmpleado.foto && (
                  <div style={{ marginTop: 10 }}>
                    <Image src={selectedEmpleado.foto} alt="Foto previa" thumbnail width={120} />
                  </div>
                )}
              </Form.Group>
              <Form.Group controlId="formNombre">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre"
                  placeholder="Ingrese el nombre"
                  value={selectedEmpleado.nombre}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
              <Form.Group controlId="formApellido">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  type="text"
                  name="apellido"
                  placeholder="Ingrese el apellido"
                  value={selectedEmpleado.apellido}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
              <Form.Group controlId="formCargo">
                <Form.Label>Cargo</Form.Label>
                <Form.Control
                  type="text"
                  name="cargo"
                  placeholder="Ingrese el cargo"
                  value={selectedEmpleado.cargo}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
              <Form.Group controlId="formDNI">
                <Form.Label>DNI</Form.Label>
                <Form.Control
                  type="text"
                  name="dni"
                  placeholder="Ingrese el DNI"
                  value={selectedEmpleado.dni}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Detalle de Empleado */}
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="md" centered>
          <Modal.Header closeButton style={{ borderBottom: 'none' }}>
            <Modal.Title style={{ fontWeight: 700, fontSize: 22, color: '#1976d2', letterSpacing: 1 }}>Empleado INPROJUY</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 28, paddingTop: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 38, color: '#1976d2', marginBottom: 8, width: '100%', display: 'flex', justifyContent: 'center' }}>
                {selectedEmpleado.foto ? (
                  <img
                    src={selectedEmpleado.foto}
                    alt="Foto del empleado"
                    style={{
                      width: 'min(38vw, 160px)',
                      height: 'min(38vw, 160px)',
                      maxWidth: 180,
                      maxHeight: 180,
                      minWidth: 90,
                      minHeight: 90,
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '2.5px solid #1976d2',
                      background: '#fff',
                      marginBottom: 4,
                      boxShadow: '0 2px 12px rgba(25,118,210,0.10)'
                    }}
                  />
                ) : (
                  <span role="img" aria-label="Empleado">👤</span>
                )}
              </div>
              <div style={{ fontWeight: 600, fontSize: 20, color: '#222', textAlign: 'center', marginBottom: 2 }}>
                {selectedEmpleado.nombre} {selectedEmpleado.apellido}
              </div>
              <div style={{ fontSize: 16, color: '#444', marginBottom: 2 }}>
                <span style={{ fontWeight: 500, color: '#888' }}>DNI:</span> {selectedEmpleado.dni}
              </div>
              <div style={{ fontSize: 16, color: '#444', marginBottom: 10 }}>
                <span style={{ fontWeight: 500, color: '#888' }}>Cargo:</span> <span style={{ color: '#1976d2', fontWeight: 600 }}>{selectedEmpleado.cargo}</span>
              </div>
              <Button
                onClick={() => setShowDetailModal(false)}
                style={{
                  background: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 17,
                  padding: '10px 32px',
                  marginTop: 10,
                  boxShadow: '0 2px 8px rgba(25,118,210,0.08)',
                  fontWeight: 600,
                  letterSpacing: 0.5
                }}
              >
                Cerrar
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </Container>
  );
};

export default BDEmpleados;